import React, { Suspense } from "react"
import ReactDOM from "react-dom"
import "./index.css"
import { Auth0Provider } from "@auth0/auth0-react"
import { init } from "@dash0/sdk-web"
import { SidebarStateProvider } from "@kamae-apps/shared/Hooks/sidebar/Context"
import { ApiStatusStateProvider } from "@kamae-apps/shared/Hooks/useApiStatus/Context"
import { CryptrProvider } from "@killiangabrielkamae/cryptr-react"
import { DevSupport } from "@react-buddy/ide-toolbox"
import posthog from "posthog-js"
import { I18nextProvider } from "react-i18next"
import { AuthProvider } from "react-oidc-context"
import { BrowserRouter } from "react-router-dom"
import App from "./App"
import { ErrorBoundary } from "./Components/ErrorBoundary/ErrorBoundary"
import { ERROR_FALLBACK_LAYOUT } from "./Components/ErrorBoundary/ErrorFallback/ErrorFallback"
import { registerPreloadErrorRecovery } from "./Components/ErrorBoundary/staleAssetsRecovery"
import AppLoader from "./Components/Organisms/Loaders/AppLoader/AppLoader"
import { ElearningContext } from "./Context"
import { SidebarModeProvider } from "./Contexts/SidebarMode/SidebarModeContext"
import { UserProvider } from "./Contexts/User/UserContext"
import i18n from "./i18n/i18n"
import { ComponentPreviews, useInitial } from "./old/dev"
import config, { auth0Config, axaOidcConf, cryptrConf } from "./variable"

// Add this condition back after testing everything works fine
if (config.env !== "development") {
  init({
    serviceName: "Webapp",
    endpoint: {
      url: "https://ingress.eu-west-1.aws.dash0.com",
      authToken: config.dash0AuthToken,
    },
    propagators: [{ type: "traceparent", match: [/^https:\/\/api\.kamae(-[^.]+)?\.(io|dojo-kamae\.com)/] }],
  })
}

posthog.init(config.posthogKey, {
  api_host: "https://q.kamae.io",
  ui_host: "https://eu.posthog.com",
  opt_in_site_apps: true,
  autocapture: false,
  capture_pageview: true,
  capture_pageleave: false,
  disable_persistence: true, // Do not use cookies to remain GDPR compliant
  disable_session_recording: true,
  advanced_disable_feature_flags_on_first_load: true,
  person_profiles: "identified_only",
  // Blacklist most properties for now to remain GDPR compliant
  property_blacklist: [
    "$os",
    "$browser",
    "$device_type",
    "$current_url",
    "$host",
    "$browser_version",
    "$lib",
    "$lib_version",
    "$referrer",
    "$referring_domain",
    "$device_id",
    "$session_id",
    "$recording_id",
    "$window_id",
    "$anon_distinct_id",
  ],
})

registerPreloadErrorRecovery()

ReactDOM.render(
  <React.StrictMode>
    <I18nextProvider i18n={i18n}>
      <ErrorBoundary layout={ERROR_FALLBACK_LAYOUT.FULL_SCREEN}>
        <ElearningContext.Provider value={new Map()}>
          <ApiStatusStateProvider>
            <AuthProvider {...axaOidcConf}>
              <CryptrProvider {...cryptrConf}>
                <Auth0Provider
                  domain={auth0Config.loginDomain}
                  clientId={auth0Config.clientId}
                  redirectUri={config.app}
                  audience={config.api}
                  scope={"read:all"}
                >
                  <BrowserRouter>
                    <UserProvider>
                      <SidebarStateProvider>
                        <SidebarModeProvider>
                          <DevSupport
                            ComponentPreviews={ComponentPreviews}
                            useInitialHook={useInitial}
                          >
                            <Suspense fallback={<AppLoader />}>
                              <App />
                            </Suspense>
                          </DevSupport>
                        </SidebarModeProvider>
                      </SidebarStateProvider>
                    </UserProvider>
                  </BrowserRouter>
                </Auth0Provider>
              </CryptrProvider>
            </AuthProvider>
          </ApiStatusStateProvider>
        </ElearningContext.Provider>
      </ErrorBoundary>
    </I18nextProvider>
  </React.StrictMode>,
  document.getElementById("kamaeRoot")
)

// TODO(KAM-3374): remove this block in December 2026 once every active browser has run it.
// Legacy PWA service worker decommissioned: unregister it and drop its caches.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then(registrations => Promise.all(registrations.map(registration => registration.unregister())))
    .then(() => caches.keys())
    .then(keys => Promise.all(keys.map(key => caches.delete(key))))
    .catch(error => {
      console.error("Error while unregistering service worker: ", error)
    })
}
